import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

export default function SearchActionsBar({
	value = '',
	onChange = () => {},
	placeholder = 'Type to search...',
	showActions = true,
	buttons = [],
	autoFocus = false,
}) {
	const inputRef = useRef(null)
	const navigate = useNavigate()

	useEffect(() => {
		if (autoFocus && inputRef.current) inputRef.current.focus()
	}, [autoFocus])

	const term = `${value ?? ''}`
	const borderClass =
		term.length === 0
			? 'border-gray-300'
			: term.length < 3
			? 'border-yellow-400 focus:ring-yellow-500'
			: 'border-green-400 focus:ring-green-500'

	const handleBtnClick = (e, btn) => {
		if (btn.onClick) btn.onClick(e)
		if (btn.to) navigate(btn.to, btn.navOptions || {})
	}

	const visibleButtons = (buttons || []).filter(btn => {
		if (btn?.type === 'delete') return (btn.count ?? 0) > 0
		return true
	})

	return (
		<div className="w-full bg-white rounded-md shadow border border-gray-100 p-2 mt-4">
			<div className="flex flex-col sm:flex-row sm:items-center gap-4">
				<div className="flex-1">
					<input
						ref={inputRef}
						type="text"
						value={term}
						onChange={onChange}
						placeholder={placeholder}
						className={`w-full rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${borderClass}`}
					/>
				</div>

				{showActions && visibleButtons.length > 0 && (
					<div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
						{visibleButtons.map((btn, i) => {
							const key = btn.key || `${btn.type}-${i}`

							if (btn.type === 'delete') {
								return (
									<button
										key={key}
										onClick={e => handleBtnClick(e, btn)}
										disabled={btn.loading}
										className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center space-x-2">
										{btn.loading ? (
											<>
												<svg
													className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24">
													<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
													/>
												</svg>
												<span>{btn.label || 'Deleting...'}</span>
											</>
										) : (
											<>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
													/>
												</svg>
												<span>{btn.label || `Delete (${btn.count})`}</span>
											</>
										)}
									</button>
								)
							}

							if (btn.type === 'date') {
								return (
									<button
										key={key}
										onClick={e => handleBtnClick(e, btn)}
										className={`px-4 py-2 rounded transition flex items-center justify-center space-x-2 ${
											btn.active
												? 'bg-green-600 text-white hover:bg-green-700'
												: 'bg-blue-600 text-white hover:bg-blue-700'
										}`}
										title={btn.title}>
										<span>{btn.label || 'Date filter'}</span>
										{btn.active && (
											<span className="inline-flex items-center justify-center w-2 h-2 bg-white rounded-full"></span>
										)}
									</button>
								)
							}

							if (btn.type === 'campaign-selected') {
								return (
									<button
										key={key}
										onClick={e => handleBtnClick(e, btn)}
										disabled={btn.disabled}
										className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center space-x-2"
										title={btn.title}>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
										<span>{btn.label || 'Selected to campaign'}</span>
									</button>
								)
							}

							if (btn.type === 'campaign-filter') {
								return (
									<button
										key={key}
										onClick={e => handleBtnClick(e, btn)}
										disabled={btn.disabled}
										className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center space-x-2"
										title={btn.title}>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
											/>
										</svg>
										<span>{btn.label || 'Filter to campaign'}</span>
									</button>
								)
							}

							if (btn.type === 'add') {
								return (
									<button
										key={key}
										onClick={e => handleBtnClick(e, btn)}
										className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition flex items-center justify-center space-x-2"
										title={btn.title}>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
										</svg>
										<span>{btn.label || 'Add new'}</span>
									</button>
								)
							}

							return (
								<button
									key={key}
									onClick={e => handleBtnClick(e, btn)}
									disabled={btn.disabled}
									className={
										btn.className ||
										'px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition flex items-center justify-center'
									}
									title={btn.title}>
									<span>{btn.label || 'Action'}</span>
								</button>
							)
						})}
					</div>
				)}
			</div>
		</div>
	)
}
